import { Inline } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { ParentChildCommonProps } from "PFComponents/experience_level/experience_level.types";
import { RequiredExperienceTooltipContent } from "PFComponents/experience_level/parts/experience_level_tooltip_content/parts/required_experience_tooltip_content";
import { Tooltip } from "PFComponents/tooltip";
import { Fragment, PropsWithChildren, useMemo } from "react";

import { CustomValuePillProps } from "../../custom_value_pill";
import css from "./custom_value_pill_tooltip.module.scss";
import { CustomValueTooltipElement } from "./custom_value_pill_tooltip.types";
import { ProfileColumnTooltipContent } from "./profile_column_tooltip_content";
import { StateTooltipContent } from "./state_tooltip_content";
import { TagsTooltipContent } from "./tags_tooltip_content";
import { VerifiedIconsTooltipContent } from "./verified_icons_tooltip_content";

type CustomValuePillTooltipProps = PropsWithChildren<
  Pick<
    CustomValuePillProps,
    | "showTooltip"
    | "experienceLevels"
    | "experienceLabelMap"
    | "experience"
    | "requiredExperience"
    | "isRequirement"
    | "isVerified"
    | "isVerifiedCredly"
    | "isVerifiedOthers"
    | "isCore"
    | "isDevelopmental"
    | "isPendingApproval"
    | "tags"
    | "customTooltipElement"
    | "tooltipElementsBlacklist"
  > &
    ParentChildCommonProps
>;

export const CustomValuePillTooltip = ({
  showTooltip,
  experienceLevels,
  experienceLabelMap,
  experience,
  requiredExperience,
  hasParentValues,
  hasChildValues,
  isRequirement,
  isVerified,
  isVerifiedCredly,
  isVerifiedOthers,
  isCore,
  isDevelopmental,
  isPendingApproval,
  tags,
  customTooltipElement,
  tooltipElementsBlacklist = [],
  children
}: CustomValuePillTooltipProps) => {
  const tooltipElements = useMemo(
    () =>
      [
        {
          id: CustomValueTooltipElement.RequiredExperience,
          show: !!requiredExperience || (isRequirement && !!experience),
          element: (
            <RequiredExperienceTooltipContent
              experienceLevels={experienceLevels}
              experienceLabelMap={experienceLabelMap}
              requiredExperience={(requiredExperience || experience)!}
            />
          )
        },
        {
          id: CustomValueTooltipElement.ProfileColumn,
          show: !isRequirement,
          element: (
            <ProfileColumnTooltipContent
              experienceLevels={experienceLevels}
              experienceLabelMap={experienceLabelMap}
              experience={experience}
              hasParentValues={hasParentValues}
              hasChildValues={hasChildValues}
              isCore={isCore}
              isDevelopmental={isDevelopmental}
            />
          )
        },
        {
          id: CustomValueTooltipElement.Tags,
          show: !!(tags || []).length,
          element: <TagsTooltipContent tags={tags || []} />
        },
        {
          id: CustomValueTooltipElement.VerifiedIcons,
          show: isVerified || isVerifiedOthers,
          element: (
            <VerifiedIconsTooltipContent
              isVerified={isVerified}
              isVerifiedCredly={isVerifiedCredly}
              isVerifiedOthers={isVerifiedOthers}
            />
          )
        },
        {
          id: CustomValueTooltipElement.State,
          show: isPendingApproval,
          element: <StateTooltipContent isPendingApproval={isPendingApproval} />
        },
        {
          id: CustomValueTooltipElement.Custom,
          show: !!customTooltipElement,
          element: customTooltipElement
        }
      ]
        .filter(({ show }) => show)
        .filter(({ id }) => !tooltipElementsBlacklist.includes(id)),
    [
      customTooltipElement,
      experience,
      experienceLabelMap,
      experienceLevels,
      hasChildValues,
      hasParentValues,
      isCore,
      isDevelopmental,
      isPendingApproval,
      isRequirement,
      isVerified,
      isVerifiedCredly,
      isVerifiedOthers,
      requiredExperience,
      tags,
      tooltipElementsBlacklist
    ]
  );

  return (
    <div className={css.tooltipWrapper}>
      <Tooltip
        disabled={!showTooltip || tooltipElements.length === 0}
        content={
          <Inline>
            {tooltipElements.map((section, index) => (
              <Fragment key={section.id}>
                {section.element}
                {index < tooltipElements.length - 1 && <Divider type="vertical" color="paletteBlue2" />}
              </Fragment>
            ))}
          </Inline>
        }
        tooltipContainerProps={{ style: { maxWidth: 600 }, className: css.smallPadding }} // just larger than default value to not crop the content
      >
        {children}
      </Tooltip>
    </div>
  );
};
